// Financial Statement Table Constants

export enum FinancialTableDataType {
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  NUMBER = "number",
  TEXT = "text",
}

export enum FinancialTableRowType {
  HEADER = "header",
  DATA = "data",
  SUMMARY = "summary",
}

// Table Styling Constants
export const FINANCIAL_TABLE_STYLES = {
  HEADER_BACKGROUND: "#000000", // "#005249", // Dark green
  HEADER_TEXT_COLOR: "#ffffff",
  DATA_ROW_BACKGROUND: "#f5f4f2", // Light beige
  DATA_ROW_TEXT_COLOR: "#000000",
  SUMMARY_ROW_BACKGROUND: "#6A0BE3", // #73AF6F // Parrot green
  SUMMARY_ROW_TEXT_COLOR: "#ffffff",
  // POSITIVE_VALUE_BACKGROUND: "#",
  POSITIVE_VALUE_BACKGROUND: "#6A0BE3", // #73AF6F
  NEGATIVE_VALUE_BACKGROUND: "#F63049",
  BORDER_COLOR: "#000000",
  // BORDER_COLOR: "#ffffff",
  BORDER_WIDTH: "1px",
  HEADER_PADDING: "7px",
  CELL_PADDING: "5px",
  HEADER_FONT_SIZE: "14px",
  CELL_FONT_SIZE: "12px",
  ROW_HEADER_DARK_BACKGROUND: "#e8e6e3", // Slightly darker beige for key rows
} as const;

// Financial Statement Table Row Keys
export const FINANCIAL_STATEMENT_ROW_KEYS = {
  // Profit & Loss Analysis
  SALES: "Sales",
  COST_OF_GOODS_SOLD: "Cost of Goods Sold",
  GROSS_PROFIT: "Gross Profit",
  DEPRECIATION_ADD_BACK: "Depreciation (add back)",
  INTEREST_EXPENSE_ADD_BACK: "Interest Expense (add back)",
  OPERATING_EXPENSES: "Operating Expenses",
  OPERATING_PROFIT_BEFORE_TAX: "Operating Profit Before Tax",
  NET_PROFIT_AFTER_TAX: "Net Profit After Tax",
  EBITDA: "EBITDA",

  // Growth Rates
  SALES_GROWTH_RATE: "Sales Growth Rate (%)",
  OPERATING_COSTS_GROWTH_RATE: "Operating Costs Growth Rate (%)",
  COGS_GROWTH_RATE: "COGS Growth Rate (%)",
  EBITDA_GROWTH_RATE: "EBITDA Growth Rate",

  // Profitability Margins
  GROSS_PROFIT_MARGIN: "Gross Profit Margin (%)",
  EBITDA_PROFIT_MARGIN: "EBITDA Profit Margin (%)",
  NET_PROFIT_MARGIN: "Net Profit Margin (%)",

  // DSCR
  EXISTING_LOAN_COMMITMENTS: "Existing Loan Commitments",
  NEW_LOAN_COMMITMENT: "New Loan Commitment",
  UPDATED_DSCR_RATIO: "Updated DSCR Ratio",

  // ICR
  EXISTING_INTEREST_REPAYMENTS: "Existing Interest repayments",
  NEW_INTEREST_REPAYMENTS: "New Interest Repayments",
  UPDATED_ICR: "Updated ICR (Operating Profit/Interest)",

  // Profitability
  UPDATED_EBITDA: "Updated EBITDA ($)",

  // Balance Sheet Analysis
  CURRENT_ASSETS: "Current Assets",
  CURRENT_LIABILITIES: "Current Liabilities",
  TOTAL_CASH_AND_EQUIVALENTS: "Total Cash and Equivalents",
  ACCOUNTS_RECEIVABLE: "Accounts Receivable",
  ACCOUNTS_PAYABLE: "Accounts Payable",
  INVENTORY: "Inventory",
  TOTAL_ASSETS: "Total Assets",
  TOTAL_LIABILITIES: "Total Liabilities",
  NET_EQUITY: "Net Equity",

  // Leverage Ratios
  DEBT_EQUITY_RATIO: "Debt/Equity (Total Liability/Total Equity)",
  DEBT_ASSET_RATIO: "Debt/Asset (Total Liability/Total Assets)",
  DEBT_EBITDA_RATIO: "Debt / EBITDA",

  // Liquidity Ratio
  CURRENT_RATIO: "Current Ratio (CA/CL)",
  CASH_RATIO: "Cash Ratio (Cash/Current Liabilities)",
  ACC_REC_AS_PERCENT_OF_SALES: "Acc. Rec as a % of Sales",

  // Efficiency Ratio
  INVENTORY_TURNOVER: "Inventory Turnover (COGS/Inventory)",
  RECEIVABLES_TURNOVER: "Receivables Turnover (Revenue/Acc. Rec.)",
  ASSET_EFFICIENCY_RATIO: "Asset Efficiency Ratio (Revenue/Total Assets)",
  PAYABLES_TURNOVER: "Payables Turnover (COGS/Accounts Payable)",

  // Cash Conversion Cycle
  DAYS_SALES_OF_INVENTORY: "Days Sales of Inventory (Inventory/COGS) x 365",
  DAYS_SALES_OUTSTANDING: "Days Sales Outstanding (Acc. Rec./Sales) x 365",
  DAYS_PAYABLE_OUTSTANDING: "Days Payable Outstanding (Acc. Pay./COGS) x 365",
  PAYABLES_TURNOVER_CASH_CYCLE: "Payables Turnover (COGS/Accounts Payable)",

  // Cashflow Analysis
  CASH_FLOW_FROM_OPERATING_ACTIVITIES: "Cash flow from Operating Activities",
  CASH_FLOW_FROM_INVESTMENT: "Cash flow from Investment",
  CASH_FLOW_FROM_FINANCING: "Cash flow from Financing",
  NET_CHANGE_IN_CASHFLOW: "Net Change in Cashflow",
} as const;

// Common column configurations for reusability
export const COMMON_FY_COLUMNS: FinancialTableColumn[] = [
  // { key: "fy2023", label: "FY2023", isHistorical: true },
  { key: "fy2023", label: "FY2023" },
  { key: "fy2024", label: "FY2024" },
  { key: "fy2025", label: "FY2025" },
  { key: "forecast2026", label: "Forecast 2026" },
] as const;

// Table Configuration Type
export interface FinancialTableColumn {
  key: string;
  label: string;
  isHistorical?: boolean; // For styling historical columns differently
}

export interface FinancialTableRow {
  key: string;
  label: string;
  type: FinancialTableRowType;
  dataType: FinancialTableDataType;
  values: (string | number | null)[];
  isSummary?: boolean; // For highlighting summary rows
  isKeyRow?: boolean; // For darker background in row header
  useConditionalColoring?: boolean; // For conditional red/green coloring based on value sign
}

export interface FinancialTableConfig {
  title: string;
  columns: FinancialTableColumn[];
  rows: FinancialTableRow[];
}
